import CaseEvidenceListDowloadModal from "@/components/cases/case-evidence-list-download-modal";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import { ScrollArea } from "@/components/core/scroller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { caseEvidenceListAllCategoriesQueryOptions } from "../api/queries/category-query";
import { caseEvidenceQueryOptions, useCaseEvidenceMutation, useEvidenceMutation } from "../api/queries/evidence-query";
import type { Defendant } from "../types/defendant";
import CaseEvidenceAllCategoryDisplayModal from "./case-evidence-all-category-display-modal";
import CaseEvidenceCategoryByCaseIdDisplayModal from "./case-evidence-category-by-case-id-display-modal";
import CaseEvidenceCategoryByPartyDisplayModal from "./case-evidence-category-by-party-display-modal";
import CaseEvidenceCategoryByTypeDisplayModal from "./case-evidence-category-by-type-display-modal";
import EvidenceList from "./evidence-list";

export default function CaseEvidencePlaintiffTab({
	caseData,
	defendant,
	depth,
	readonly = false,
	index,
}: {
	caseData: CaseRecord;
	defendant?: Defendant;
	readonly: boolean;
	depth: string;
	index: number;
}) {
	const modal = useModal();
	const [isEditMode, setIsEditMode] = useState(false);
	const [tabValue, setTabValue] = useState("Documentary");
	const [_, setHasData] = useState(false);
	const [isDocumentary, setIsDocumentary] = useState(null);
	const [isTestimonial, setIsTestimonial] = useState(null);
	const [isObject, setIsObject] = useState(null);
	const caseEvidenceMutation = useCaseEvidenceMutation();
	const expungeCaseMutation = useEvidenceMutation();

	const plaintiffDocumetaryEvidenceQuery = useQuery(
		caseEvidenceQueryOptions({
			parties: "Plaintiff",
			evidence_type: "Documentary",
			case_id: caseData.id,
			case_record_user_id: defendant?.id,
		}),
	);

	const plaintiffObjectEvidenceQuery = useQuery(
		caseEvidenceQueryOptions({
			parties: "Plaintiff",
			evidence_type: "Object",
			case_id: caseData.id,
			case_record_user_id: defendant?.id,
		}),
	);

	const plaintiffTestimonialEvidenceQuery = useQuery(
		caseEvidenceQueryOptions({
			parties: "Plaintiff",
			evidence_type: "Testimonial",
			case_id: caseData.id,
			case_record_user_id: defendant?.id,
		}),
	);

	const plaintiffTestimonial = plaintiffTestimonialEvidenceQuery.data;
	const plaintiffObject = plaintiffObjectEvidenceQuery.data;
	const plaintiffDocumentary = plaintiffDocumetaryEvidenceQuery.data;

	useEffect(() => {
		const fetchData = async () => {
			if (plaintiffTestimonial?.data.length === 0) {
				setHasData(false);
			} else {
				setHasData(true);
			}
		};

		fetchData();
	}, []);

	const changeActivetab = (value: string) => {
		setTabValue(value);
		switch (value) {
			case "Testimonial":
				if (plaintiffTestimonial?.data.length === 0) {
					setHasData(false);
				} else {
					setHasData(true);
				}
				break;

			case "Object":
				if (plaintiffObject?.data.length === 0) {
					setHasData(false);
				} else {
					setHasData(true);
				}
				break;

			case "Documentary":
				if (plaintiffDocumentary?.data.length === 0) {
					setHasData(false);
				} else {
					setHasData(true);
				}
				break;

			default:
				if (plaintiffDocumentary?.data.length === 0) {
					setHasData(false);
				} else {
					setHasData(true);
				}
				break;
		}
	};
	const showEvidenceListByType = (category: string) => {
		modal.openModal({
			title: `Pieces of Evidence: Category (${category})`,
			size: "lg",
			showFooter: false,
			component: <CaseEvidenceCategoryByTypeDisplayModal category={category} evidenceType={tabValue} parties="Plaintiff" caseData={caseData} />,
		});
	};
	const showEvidenceListByCaseId = (category: string) => {
		modal.openModal({
			title: `Pieces of Evidence: Category (${category})`,
			size: "lg",
			showFooter: false,
			component: (
				<CaseEvidenceCategoryByCaseIdDisplayModal category={category} evidenceType={tabValue} caseId={caseData.id} parties="Plaintiff" caseData={caseData} />
			),
		});
	};
	const showEvidenceListByParties = (category: string) => {
		modal.openModal({
			title: `Pieces of Evidence: Category (${category})`,
			size: "lg",
			showFooter: false,
			component: <CaseEvidenceCategoryByPartyDisplayModal category={category} evidenceType={tabValue} parties="Plaintiff" caseData={caseData} />,
		});
	};
	const showEvidenceListWithCategory = (category: string) => {
		modal.openModal({
			title: `Pieces of Evidence: Category (${category})`,
			size: "lg",
			showFooter: false,
			component: <CaseEvidenceAllCategoryDisplayModal category={category} evidenceType={tabValue} parties="Plaintiff" caseData={caseData} />,
		});
	};

	const allCategoryQuery = useSuspenseQuery(
		caseEvidenceListAllCategoriesQueryOptions({
			case_id: caseData.id,
		}),
	);
	return (
		<Tabs className="flex flex-col w-full h-full" variant="compact" defaultValue={tabValue} onValueChange={async (value) => await changeActivetab(value)}>
			<div className="flex flex-col w-full">
				<div className="flex flex-row items-center w-full p-4 gap-2">
					<div className="flex flex-row justify-between items-center w-full">
						<div className="flex flex-row gap-2 items-center w-full">
							{/* badge */}
							<div className="inline-flex justify-center items-center shrink-0 w-[20px] h-[20px] rounded bg-blue-500">
								<span className="uppercase text-sm font-semibold text-white">P</span>
							</div>
							{/* names */}
							<div>
								{defendant ? (
									<span className="text-base font-semibold text-foreground/90">{defendant?.user?.full_name}</span>
								) : caseData.plaintiffs.length !== 1 ? (
									caseData.plaintiffs?.map((plaintiff, index, arr) => (
										<span key={plaintiff.id} className="text-base font-semibold text-foreground/90">
											{plaintiff?.full_name}
											{index === caseData.plaintiffs.length - 2 ? " and " : index < caseData.plaintiffs.length - 2 ? ", " : ""}
										</span>
									))
								) : (
									<span className="text-base font-semibold text-foreground/90">{caseData.plaintiffs[0]?.user?.full_name}</span>
								)}
							</div>
						</div>
						<div className="flex flex-row items-center gap-2 ml-auto">
							{/* sort dropdown */}
							<div className="flex flex-row gap-2 items-center w-full">
								<span className="text-sm text-foreground/70">Sort:</span>
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button size="sm" variant="outline">
											<span>By Category</span>
											<IconChevronDown size={14} stroke={2} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										{allCategoryQuery?.data.map((value: string, index: number) => (
											<DropdownMenuSub key={index}>
												<DropdownMenuSubTrigger>
													<div className="flex flex-row items-center gap-2 p-1 text-sm">
														<span>{value}</span>
													</div>
													<DropdownMenuShortcut>
														<IconChevronRight size={18} stroke={1.5} />
													</DropdownMenuShortcut>
												</DropdownMenuSubTrigger>

												<DropdownMenuPortal>
													<DropdownMenuSubContent sideOffset={6} alignOffset={-4}>
														<DropdownMenuItem
															onClick={() => {
																showEvidenceListWithCategory(value);
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 text-sm">All Evidence</div>
														</DropdownMenuItem>
														<DropdownMenuItem
															onClick={() => {
																showEvidenceListByType(value);
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 text-sm">By Evidence Type</div>
														</DropdownMenuItem>
														<DropdownMenuItem
															onClick={() => {
																showEvidenceListByCaseId(value);
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 text-sm">By Case</div>
														</DropdownMenuItem>
														<DropdownMenuItem
															onClick={() => {
																showEvidenceListByParties(value);
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 text-sm">By Party</div>
														</DropdownMenuItem>
													</DropdownMenuSubContent>
												</DropdownMenuPortal>
											</DropdownMenuSub>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
							{/* dropdown */}
							<div className="flex flex-row w-full justify-end gap-2">
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button className="" size="sm" variant="ghost" type="button">
											<span className="">Generate List</span>
											<IconChevronDown size={16} stroke={2.5} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<DropdownMenuItem
											onClick={() => {
												modal.openModal({
													title: "Generate Evidence List",
													showFooter: false,
													size: "md",
													component: <CaseEvidenceListDowloadModal caseId={caseData.id} parties="Plaintiff" evidenceType={tabValue} both={true} />,
												});
											}}
										>
											<div className="flex flex-row items-center gap-2 px-2 py-1">
												<span>Both</span>
											</div>
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => {
												modal.openModal({
													title: "Generate Evidence List",
													showFooter: false,
													size: "md",
													component: <CaseEvidenceListDowloadModal caseId={caseData.id} parties="Plaintiff" evidenceType={tabValue} both={false} />,
												});
											}}
										>
											<div className="flex flex-row items-center gap-2 px-2 py-1">
												<span>Plaintiff</span>
											</div>
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => {
												modal.openModal({
													title: "Generate Evidence List",
													showFooter: false,
													size: "md",
													component: <CaseEvidenceListDowloadModal caseId={caseData.id} parties="Defendant" evidenceType={tabValue} both={false} />,
												});
											}}
										>
											<div className="flex flex-row items-center gap-2 px-2 py-1">
												<span>Defendant</span>
											</div>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					</div>
				</div>

				<TabsList className="">
					<TabsTrigger value="Documentary" disabled={isTestimonial || isObject}>
						Documents ({plaintiffDocumentary?.data.length || 0})
					</TabsTrigger>
					<TabsTrigger value="Testimonial" disabled={isDocumentary || isObject}>
						Testimonial ({plaintiffTestimonial?.data.length || 0})
					</TabsTrigger>
					<TabsTrigger value="Object" disabled={isTestimonial || isDocumentary}>
						Object ({plaintiffObject?.data.length || 0})
					</TabsTrigger>
				</TabsList>
			</div>

			{/* Documentary */}
			<TabsContent className="flex flex-col w-full h-full" value="Documentary">
				<div className="flex shrink grow flex-col w-full h-full">
					<ScrollArea className="flex flex-col w-full">
						<div className="flex flex-col w-full">
							{plaintiffDocumentary?.data && (
								<EvidenceList
									key={JSON.stringify(plaintiffDocumentary?.data)}
									onEditMode={(isEdit) => {
										setIsEditMode(isEdit);
										setIsDocumentary(isEdit);
									}}
									className={clsx("flex flex-col w-full gap-2 overflow-y-auto", isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]")}
									schema={[
										{
											className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
											name: "evidence_attachment",
											label: "File Name",
											type: "dragged_file",
										},
										{
											name: "exhibit",
											label: "Exhibit",
											type: "text_input",
											placeholder: "Enter exhibit",
										},
										{
											name: "description",
											label: "Description",
											type: "textarea",
											placeholder: "Enter description",
										},
										{
											name: "purpose",
											label: "Purpose",
											type: "textarea",
											placeholder: "Enter purpose",
										},
										{
											name: "marking_status",
											label: "Marking Status",
											type: "marking_status",
										},
										{
											name: "admission_status",
											label: "Admission Status",
											type: "admission_status",
										},
										{
											name: "category",
											label: "Category",
											type: "textarea",
											placeholder: "Enter category",
										},
									]}
									data={plaintiffDocumentary?.data.map((data) => {
										return {
											...data,
											file: data.evidence,
										};
									})}
									onRowDelete={async (row) => {
										await expungeCaseMutation.mutateAsync({
											case_id: caseData.id,
											status: "Expunged",
											evidence_type: "Documentary",
											parties: "Plaintiff",
											...row,
										});
									}}
									onSubmit={async (files) => {
										console.log(files, "files");
										await caseEvidenceMutation.mutateAsync({
											case_id: caseData.id,
											case_record_user_id: defendant?.id,
											evidence_type: "Documentary",
											parties: "Plaintiff",
											evidences: files,
										});
									}}
									metadata={{
										evidence_type: "Documentary",
										case_record_user_id: defendant?.id,
										parties: "Plaintiff",
									}}
									caseData={caseData}
								/>
							)}
						</div>
					</ScrollArea>
				</div>
			</TabsContent>

			{/* Testimonial */}
			<TabsContent value="Testimonial">
				<div className="flex shrink grow flex-col w-full h-full">
					<ScrollArea className="flex flex-col w-full">
						<div className="flex flex-col w-full">
							<EvidenceList
								key={JSON.stringify(plaintiffTestimonial?.data)}
								onEditMode={(isEdit) => {
									setIsEditMode(isEdit);
									setIsTestimonial(isEdit);
								}}
								className={clsx("flex flex-col w-full gap-2 overflow-y-auto", isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]")}
								schema={[
									{
										name: "witness_name",
										label: "Witness Name",
										type: "text_input",
										placeholder: "Enter witness name",
									},
									{
										name: "description",
										label: "Description",
										type: "textarea",
										placeholder: "Enter description",
									},
									{
										name: "stenography",
										label: "Transcript of Stenographic Notes",
										type: "file",
									},
									{ name: "category", label: "Category", type: "textarea", placeholder: "Enter category" },
								]}
								data={plaintiffTestimonial?.data.map((data) => {
									return {
										...data,
										file: data.evidence,
									};
								})}
								onRowDelete={async (row) => {
									await expungeCaseMutation.mutateAsync({
										case_id: caseData.id,
										status: "Expunged",
										evidence_type: "Testimonial",
										parties: "Plaintiff",
										...row,
									});
								}}
								onSubmit={async (files) => {
									await caseEvidenceMutation.mutateAsync({
										case_id: caseData.id,
										case_record_user_id: defendant?.id,
										evidence_type: "Testimonial",
										parties: "Plaintiff",
										evidences: files,
										stenography: files.stenography,
									});
								}}
								caseData={caseData}
								metadata={{
									evidence_type: "Testimonial",
									case_record_user_id: defendant?.id,
									parties: "Plaintiff",
								}}
							/>
						</div>
					</ScrollArea>
				</div>
			</TabsContent>

			{/* Object */}
			<TabsContent value="Object">
				<div className="flex shrink grow flex-col w-full h-full">
					<ScrollArea className="flex flex-col w-full">
						<div className="flex flex-col w-full">
							<EvidenceList
								key={JSON.stringify(plaintiffObject?.data)}
								onEditMode={(isEdit) => {
									setIsEditMode(isEdit);
									setIsObject(isEdit);
								}}
								className={clsx("flex flex-col w-full gap-2 overflow-y-auto", isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]")}
								schema={[
									{
										className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
										name: "evidence_attachment",
										label: "File Name",
										type: "dragged_file",
									},
									{
										name: "exhibit",
										label: "Exhibit",
										type: "text_input",
										placeholder: "Enter exhibit",
									},
									{
										name: "description",
										label: "Description",
										type: "textarea",
										placeholder: "Enter description",
									},
									{
										name: "purpose",
										label: "Purpose",
										type: "textarea",
										placeholder: "Enter purpose",
									},
									{
										name: "marking_status",
										label: "Marking Status",
										type: "marking_status",
									},
									{
										name: "admission_status",
										label: "Admission Status",
										type: "admission_status",
									},
									{ name: "category", label: "Category", type: "textarea", placeholder: "Enter category" },
								]}
								data={plaintiffObject?.data.map((data) => {
									return {
										...data,
										file: data.evidence,
									};
								})}
								onRowDelete={async (row) => {
									await expungeCaseMutation.mutateAsync({
										case_id: caseData.id,
										status: "Expunged",
										evidence_type: "Object",
										parties: "Plaintiff",
										...row,
									});
								}}
								caseData={caseData}
								metadata={{
									evidence_type: "Object",
									case_record_user_id: defendant?.id,
									parties: "Plaintiff",
								}}
								onSubmit={async (files) => {
									await caseEvidenceMutation.mutateAsync({
										case_id: caseData.id,
										case_record_user_id: defendant?.id,
										evidence_type: "Object",
										parties: "Plaintiff",
										evidences: files,
									});
								}}
							/>
						</div>
					</ScrollArea>
				</div>
			</TabsContent>
		</Tabs>
	);
}
